/**
 * FahrzeugscheinScanStep — Schritt 2 des Kiosk-Wizards
 *
 * Vollbild-Kamera mit VisionKit-Style Overlay-Rahmen und animiertem Scan-Streifen.
 * Nach Foto-Capture: 2-Sek-Animation "Daten werden erkannt", dann Erfolgsanzeige.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, CheckCircle2, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import type { FahrzeugscheinFelder } from '@/lib/kiosk-ocr';

type ScanPhase = 'camera' | 'analyzing' | 'success' | 'error' | 'nocamera';

interface Props {
  onComplete: (felder: FahrzeugscheinFelder, fahrzeugscheinUrl: string, sessionId: string) => void;
  onBack: () => void;
}

const BRAND_BLUE = '#1E5FB7';
const BRAND_GREEN = '#3DB54A';

export function FahrzeugscheinScanStep({ onComplete, onBack }: Props) {
  const [phase, setPhase] = useState<ScanPhase>('camera');
  const [errorMsg, setErrorMsg] = useState('');
  const [felder, setFelder] = useState<FahrzeugscheinFelder | null>(null);
  const [scanResult, setScanResult] = useState<{ url: string; sessionId: string } | null>(null);
  // Preserved across retries so subsequent scans update the same kiosk_sessions row
  const sessionIdRef = useRef<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let active = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch {
        if (active) setPhase('nocamera');
      }
    }

    if (phase === 'camera') startCamera();

    return () => {
      active = false;
      if (phase !== 'camera') stopCamera();
    };
  }, [phase, stopCamera]);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const captureAndAnalyze = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d')?.drawImage(video, 0, 0);

    const base64 = canvas.toDataURL('image/jpeg', 0.92).replace('data:image/jpeg;base64,', '');

    stopCamera();
    setPhase('analyzing');

    try {
      const res = await fetch('/api/kiosk/ocr-fahrzeugschein', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: base64,
          mimeType: 'image/jpeg',
          session_id: sessionIdRef.current ?? undefined,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      sessionIdRef.current = data.session_id ?? sessionIdRef.current;
      setFelder(data.felder);
      setScanResult({ url: data.fahrzeugschein_url ?? '', sessionId: data.session_id ?? '' });
      setPhase('success');
      return { felder: data.felder, url: data.fahrzeugschein_url, sessionId: data.session_id };
    } catch (err) {
      console.error('[FahrzeugscheinScan] OCR-Fehler:', err);
      setErrorMsg('Die Analyse ist fehlgeschlagen. Bitte erneut versuchen.');
      setPhase('error');
      return null;
    }
  }, [stopCamera]);

  const retry = useCallback(() => {
    setErrorMsg('');
    setFelder(null);
    setScanResult(null);
    setPhase('camera');
  }, []);

  return (
    <div data-testid="fahrzeugschein-scan-step" className="flex flex-col items-center gap-6 w-full">
      <AnimatePresence mode="wait">

        {/* ── Kamera-Phase ──────────────────────────────────────── */}
        {(phase === 'camera' || phase === 'nocamera') && (
          <motion.div
            key="camera"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-5 w-full"
          >
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
                style={{ background: BRAND_BLUE }}
              >
                <Camera size={26} className="text-white" />
              </div>
              <h2 className="text-[22px] font-bold text-gray-900">Fahrzeugschein scannen</h2>
              <p className="text-[14px] text-gray-500 mt-1 max-w-xs mx-auto leading-relaxed">
                Legen Sie Ihren Fahrzeugschein flach hin und positionieren Sie ihn im Rahmen.
              </p>
            </div>

            {phase === 'nocamera' ? (
              <div className="w-full bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 text-center">
                <AlertCircle size={32} className="text-amber-500 mx-auto mb-3" />
                <p className="text-[14px] font-semibold text-amber-800 mb-1">Kamerazugriff nicht möglich</p>
                <p className="text-[13px] text-amber-700">
                  Bitte erlauben Sie den Kamerazugriff in den Browser-Einstellungen.
                </p>
              </div>
            ) : (
              /* Camera viewfinder with VisionKit-style overlay */
              <div
                className="relative rounded-2xl overflow-hidden bg-black w-full shadow-xl"
                style={{ aspectRatio: '4/3', maxHeight: 340 }}
                data-testid="camera-viewfinder"
              >
                {/* Live video */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Dark overlay with document cutout effect */}
                <div className="absolute inset-0 bg-black/40" />

                {/* VisionKit-style document frame */}
                <div
                  className="absolute"
                  data-testid="scan-overlay-frame"
                  style={{
                    top: '10%', left: '8%',
                    width: '84%', height: '80%',
                    border: '2px solid rgba(255,255,255,0.9)',
                    borderRadius: 8,
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                  }}
                >
                  {/* Corner markers */}
                  {[
                    { top: -2, left: -2, borderRight: 'none', borderBottom: 'none' },
                    { top: -2, right: -2, borderLeft: 'none', borderBottom: 'none' },
                    { bottom: -2, left: -2, borderRight: 'none', borderTop: 'none' },
                    { bottom: -2, right: -2, borderLeft: 'none', borderTop: 'none' },
                  ].map((style, i) => (
                    <div
                      key={i}
                      className="absolute"
                      style={{
                        width: 20, height: 20,
                        border: `3px solid ${BRAND_BLUE}`,
                        borderRadius: 2,
                        ...style,
                      }}
                    />
                  ))}

                  {/* Animated scan stripe */}
                  <motion.div
                    className="absolute left-0 right-0 h-0.5"
                    style={{ background: `linear-gradient(90deg, transparent, ${BRAND_BLUE}, transparent)` }}
                    animate={{ top: ['5%', '95%', '5%'] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                  />
                </div>

                <p className="absolute bottom-3 left-0 right-0 text-center text-white text-[12px] font-medium tracking-wide">
                  Fahrzeugschein im Rahmen positionieren
                </p>
              </div>
            )}

            <canvas ref={canvasRef} className="hidden" />

            <div className="flex gap-3 w-full justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
                style={{ minHeight: 52 }}
              >
                <ArrowLeft size={16} /> Zurück
              </button>

              <button
                onClick={captureAndAnalyze}
                disabled={phase === 'nocamera'}
                data-testid="capture-button"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
              >
                <Camera size={18} />
                Foto aufnehmen
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Analyse-Phase ─────────────────────────────────────── */}
        {phase === 'analyzing' && (
          <motion.div
            key="analyzing"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-6 py-8 text-center"
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)` }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <RefreshCw size={36} className="text-white" />
              </motion.div>
            </div>
            <div>
              <h2 className="text-[24px] font-bold text-gray-900">Daten werden erkannt</h2>
              <p className="text-[15px] text-gray-500 mt-2">
                Fahrzeugschein wird analysiert…
              </p>
            </div>
            {/* Pulsing dots */}
            <div className="flex gap-2 mt-2">
              {[0, 1, 2].map(i => (
                <motion.div
                  key={i}
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: BRAND_BLUE }}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.4 }}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Erfolgs-Phase ─────────────────────────────────────── */}
        {phase === 'success' && felder && (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-5 w-full"
          >
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                style={{ background: '#ECFDF5' }}
              >
                <CheckCircle2 size={36} className="text-emerald-600" />
              </motion.div>
              <h2 className="text-[22px] font-bold text-gray-900">Fahrzeugschein erkannt</h2>
              <p className="text-[14px] text-gray-500 mt-1">
                Bitte prüfen Sie die erkannten Daten.
              </p>
            </div>

            <div
              className="bg-white rounded-2xl border-2 border-gray-200 p-5 space-y-3 shadow-sm"
              data-testid="ocr-result-fields"
            >
              <FelderRow label="Kennzeichen" value={felder.kennzeichen} />
              <FelderRow label="FIN" value={felder.fin} mono />
              <FelderRow label="Halter" value={felder.halter} />
              <FelderRow label="Erstzulassung" value={felder.erstzulassung} />
              <FelderRow label="HSN / TSN" value={felder.hsn && felder.tsn ? `${felder.hsn} / ${felder.tsn}` : felder.hsn || felder.tsn} />
            </div>

            <p className="text-[12px] text-gray-400 text-center">
              Sie können alle Felder im nächsten Schritt korrigieren.
            </p>

            <div className="flex gap-3">
              <button
                onClick={retry}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
                style={{ minHeight: 52 }}
              >
                <RefreshCw size={16} /> Erneut scannen
              </button>

              <button
                onClick={() => felder && onComplete(felder, scanResult?.url ?? '', scanResult?.sessionId ?? '')}
                data-testid="confirm-ocr-button"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
                style={{ background: `linear-gradient(135deg, ${BRAND_GREEN}, #2D9E3A)`, minHeight: 52 }}
              >
                <CheckCircle2 size={18} />
                Weiter
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Fehler-Phase ──────────────────────────────────────── */}
        {phase === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-5 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center shadow-md">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-[20px] font-bold text-gray-900">Scan fehlgeschlagen</h2>
              <p className="text-[14px] text-gray-500 mt-1">{errorMsg}</p>
            </div>
            <button
              onClick={retry}
              data-testid="retry-button"
              className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
              style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
            >
              <RefreshCw size={18} /> Erneut versuchen
            </button>
            <button
              onClick={onBack}
              className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              Zurück
            </button>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
}

function FelderRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[12px] text-gray-400 w-28 flex-shrink-0">{label}</span>
      <span
        className={`text-[14px] font-semibold text-gray-800 flex-1 ${mono ? 'font-mono' : ''}`}
      >
        {value || <span className="text-gray-300 font-normal">—</span>}
      </span>
    </div>
  );
}
